import './MonitorMenu.scss';

import classNames from 'classnames';
import { createAsyncSelectors } from '@lib/selectors';
import { MonitorApplication } from './MonitorApplication';
import { MonitorDetails } from './MonitorDetails';
import { MonitorNotifications } from './MonitorNotifications';
import { Space } from 'antd';
import { useDataSecurityMonitors } from '@hooks/data-security/useDataSecurityMonitors';
import { VisualPicker } from '@ui/form-items';
import { When } from 'react-if';

import { ActionTemplateCard, labelMetaLookup } from '../ActionTemplateCard';
import {
	ClassificationLevel,
	coreActions,
	DataSecurityMonitor,
	DataSecurityMonitorType,
	FeatureFlag,
	getRecommendedActionTemplateById,
	MonitorFormTypes,
	MonitoringWindowType,
	RecommendedActionTemplateDetails
} from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, SETTINGS_DATA_SECURITY_COPY } from '@sonar-software/sonar-ts-constants';
import { isEqual, isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useAction,
	useAsyncState,
	useDataSecurityApps,
	useFeatureFlag,
	useFeatureFlags,
	useOnMount,
	usePrevious
} from '@hooks';

const selectors = createAsyncSelectors((state) => state.core.dataSecurity.actionTemplates.get);

export type MenuOptions = 'all' | 'applicationRequired' | 'noApplicationRequired' | 'hide';

export interface MonitorMenuProps {
	editing?: boolean,
	fallbackMonitor?: MonitorFormTypes;
	fieldId?: number,
	hideApplications?: boolean,
	menuOptions?: MenuOptions,
	monitor?: MonitorFormTypes,
	onChange?: (monitor: MonitorFormTypes) => void,
	overrideMonitors?: Array<MonitorFormTypes>,
	showRecommendedActionTemplates?: boolean
}

interface MonitorInfo {
	active: boolean,
	applicationRequired: boolean,
	default: MonitorFormTypes,
	disabled?: boolean,
	featureFlag?: FeatureFlag,
	intrinsic: boolean,
	max: number,
	title: string,
	type: DataSecurityMonitorType
}

export const defaultConfig : MonitorFormTypes = {
	applicationId: undefined,
	applicationIds: [],
	monitorConfig: {
		complianceCategories: undefined,
		dataSensitivityLevels: undefined,
		enableOnWeekends: undefined,
		monitorTypeId: undefined,
		monitoringWindow: undefined,
		ruleType: undefined,
		rules: undefined
	},
	name: '',
	overrideCount: undefined,
	stakeholderIds: [],
	templateId: undefined
};

export const monitorMenuConfigs: Array<MonitorInfo> = [
	{
		active: true,
		applicationRequired: true,
		default: {
			...defaultConfig,
			monitorConfig: {
				...defaultConfig.monitorConfig,
				blockAppsWhenAccessChanges: false,
				monitorTypeId: DataSecurityMonitorType.NewObjectAndFieldAccess
			},
			name: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS
		},
		featureFlag: FeatureFlag.DataSecurityDashboard,
		intrinsic: true,
		max: 1,
		title: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS,
		type: DataSecurityMonitorType.NewObjectAndFieldAccess
	},
	{
		active: true,
		applicationRequired: true,
		default: {
			...defaultConfig,
			monitorConfig: {
				...defaultConfig.monitorConfig,
				enableOnWeekends: false,
				monitorTypeId: DataSecurityMonitorType.Uptime,
				monitoringWindow: MonitoringWindowType.Daily
			}
		},
		featureFlag: FeatureFlag.DataSecurityDashboard,
		intrinsic: false,
		max: 1,
		title: SETTINGS_DATA_SECURITY_COPY.UPTIME,
		type: DataSecurityMonitorType.Uptime
	},
	{
		active: true,
		applicationRequired: true,
		default: {
			...defaultConfig,
			monitorConfig: {
				...defaultConfig.monitorConfig,
				monitorTypeId: DataSecurityMonitorType.NewUser
			}
		},
		intrinsic: false,
		max: 1,
		title: SETTINGS_DATA_SECURITY_COPY.NEW_USER,
		type: DataSecurityMonitorType.NewUser
	},
	{
		active: true,
		applicationRequired: false,
		default: {
			...defaultConfig,
			monitorConfig: {
				...defaultConfig.monitorConfig,
				monitorTypeId: DataSecurityMonitorType.ConfigurableFieldAccess
			}
		},
		featureFlag: FeatureFlag.DataSecurityDashboard,
		intrinsic: false,
		max: -1,
		title: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS,
		type: DataSecurityMonitorType.ConfigurableFieldAccess
	},
	{
		active: true,
		applicationRequired: false,
		default: {
			...defaultConfig,
			monitorConfig: {
				...defaultConfig.monitorConfig,
				monitorTypeId: DataSecurityMonitorType.SetupAuditTrail,
				ruleType: 'and',
				rules: [ {
					condition: 'classification',
					value: [ ClassificationLevel.Critical ]
				} ]
			}
		},
		featureFlag: FeatureFlag.SetupAuditTrail,
		intrinsic: false,
		max: -1,
		title: SETTINGS_DATA_SECURITY_COPY.SETUP_AUDIT_TRAIL,
		type: DataSecurityMonitorType.SetupAuditTrail
	}
];

export function getMenuItems(
	monitors: Array<MonitorFormTypes>,
	isEnabled: (flag: FeatureFlag | FeatureFlag[] | undefined) => boolean,
	applicationIds?: Array<number>,
	type: MenuOptions = 'all'
) {
	const configList = monitorMenuConfigs.filter((x) =>
		!x.intrinsic &&
		x.active &&
		((x.featureFlag && isEnabled(x.featureFlag)) || !x.featureFlag) &&
		((type === 'applicationRequired' && x.applicationRequired) ||
			(type === 'noApplicationRequired' && !x.applicationRequired) ||
			(type === 'all')));

	const menuItems = configList.reduce((result, config) => {
		if (config.max === -1) {
			config.disabled = false;
		} else {
			config.disabled = config.applicationRequired && applicationIds ?
				applicationIds.every((appId) =>
					monitors.filter((monitor) =>
						monitor.monitorConfig?.monitorTypeId === config.type &&
					monitor.applicationId === appId).length >= config.max) :
				monitors.filter((monitor) =>
					monitor.monitorConfig?.monitorTypeId === config.type).length >= config.max;
		}

		return [ ...result, config ];

	}, []);

	return menuItems;
}

async function getTemplateDetails(templateId: number): Promise<RecommendedActionTemplateDetails | undefined> {
	const result = await getRecommendedActionTemplateById({
		templateId
	});

	return result.data;
};

export const MonitorMenu: React.FC<MonitorMenuProps> = ({
	editing = true,
	fallbackMonitor,
	fieldId,
	hideApplications = false,
	menuOptions,
	monitor,
	onChange,
	overrideMonitors,
	showRecommendedActionTemplates = false
}) => {
	const monitors = useDataSecurityMonitors();
	const applications = useDataSecurityApps();
	const { isEnabled } = useFeatureFlags();
	const isTemplatesEnabled = useFeatureFlag(FeatureFlag.RecommendedActionTemplates);
	const load = useAction(coreActions.requestGetCoreDataSecurityActionTemplates);

	const { data } = useAsyncState(selectors);

	useOnMount(() => {
		if (isTemplatesEnabled && !data) {
			load();
		}
	});

	const isExistingMonitor =
		Boolean(monitor?.id || !editing || (fieldId &&
		((overrideMonitors?.length === 1 &&
			overrideMonitors[ 0 ].monitorConfig?.monitorTypeId === monitor?.monitorConfig?.monitorTypeId) ||
		(monitor?.monitorConfig?.monitorTypeId && monitorMenuConfigs
			.find((x) => x.type === monitor.monitorConfig?.monitorTypeId)?.intrinsic))));

	const monitorTypeOptions = useMemo(() => {
		if (isExistingMonitor) {
			return [ {
				disabled: false,
				label: monitorMenuConfigs.find((x) => x.type === monitor?.monitorConfig?.monitorTypeId)?.title ?? '',
				value: monitor?.monitorConfig?.monitorTypeId ?? 1
			} ];
		}

		const menuItems = getMenuItems(
			overrideMonitors ?? monitors,
			isEnabled,
			hideApplications && monitor?.applicationId ?
				[ monitor?.applicationId ] :
				applications.map((app) => app.applicationId),
			menuOptions
		);

		return menuItems.map((config) => (
			{
				disabled: config.disabled,
				label: config.title,
				value: config.type
			}
		));
	}, [
		applications,
		hideApplications,
		isExistingMonitor,
		isEnabled,
		menuOptions,
		monitor,
		monitors,
		overrideMonitors
	]);

	const defaultMonitor = useMemo(() => {
		if (monitor?.monitorConfig?.monitorTypeId) {
			return monitor;
		}

		const validOptions = monitorTypeOptions.filter((x) => x.disabled === false);

		const tempMonitor =
			validOptions.length === 1 && monitorMenuConfigs.find((x) => x.type === validOptions[ 0 ].value)?.default ?
				{ ...monitorMenuConfigs.find((x) => x.type === validOptions[ 0 ].value)?.default } :
				{ ...defaultConfig };

		if (monitor?.applicationId) {
			tempMonitor.applicationId = monitor.applicationId;
		}

		return tempMonitor;

	}, [ monitor, monitorTypeOptions ]);

	const [ currentMonitor, setCurrentMonitor ] = useState(defaultMonitor);
	const prevDefault = usePrevious(defaultMonitor, defaultMonitor);

	const selectedMonitorMenuConfig = useMemo(() =>
		monitorMenuConfigs.find((x) => x.type === currentMonitor?.monitorConfig?.monitorTypeId)
	, [ currentMonitor ]);

	const setSelectedMonitor = useCallback((newMonitor: MonitorFormTypes) => {
		if (!isEqual(newMonitor, currentMonitor)) {
			setCurrentMonitor(newMonitor);

			if (onChange) {
				onChange(newMonitor);
			}
		}
	}, [ currentMonitor, onChange ]);

	useEffect(() => {
		if (!isEqual(defaultMonitor, prevDefault)) {
			setSelectedMonitor(defaultMonitor);
		}
	}, [ defaultMonitor, prevDefault, setSelectedMonitor ]);

	const handleOnTypeChange = useCallback((type: DataSecurityMonitorType) => {
		if (type === undefined) {
			setSelectedMonitor(fallbackMonitor ?? defaultConfig);

			return;
		}

		const newMonitor = { ...monitorMenuConfigs.find((config) => config.type === type)?.default ?? defaultConfig };

		if (defaultMonitor?.applicationId) {
			newMonitor.applicationId = defaultMonitor.applicationId;
		}

		setSelectedMonitor(newMonitor);

	}, [ defaultMonitor.applicationId, fallbackMonitor, setSelectedMonitor ]);

	const handleOnTemplateChange = useCallback(async(templateId: number) => {
		if (!templateId) {
			if (monitor?.monitorConfig?.monitorTypeId) {
				handleOnTypeChange(monitor.monitorConfig.monitorTypeId);
			} else {
				setSelectedMonitor(fallbackMonitor ?? defaultConfig);
			}

			return;
		}

		const templateDetails = await getTemplateDetails(templateId);

		if (!templateDetails) {
			return;
		}

		const newMonitor = {
			applicationIds: templateDetails.integratedApplicationIds ?? [],
			monitorConfig: {
				...templateDetails.configuration
			},
			overrideCount: templateDetails.overrideCount,
			templateId
		} as MonitorFormTypes;

		setSelectedMonitor(newMonitor);
	}, [ fallbackMonitor, setSelectedMonitor ]);

	const templateTypeOptions = useMemo(() => {
		const selectedType = currentMonitor.monitorConfig?.monitorTypeId;
		const templateList = selectedType ?
			data?.filter((template) => selectedType === labelMetaLookup[ template.label ]?.monitorType) :
			data?.filter((template) => template.type === 'Monitor');

		return templateList?.map((template) => (
			{
				disabled: template.disabled,
				label: <ActionTemplateCard
					key={ template.id }
					onClick={ () => handleOnTemplateChange(template.id) }
					{ ...template } />,
				value: template.id
			}
		)) ?? [];
	}, [ currentMonitor, data, handleOnTemplateChange ]);

	return (
		<Space
			className={ classNames({ 'hide-first': menuOptions === 'hide' }) }
			direction='vertical'>
			<VisualPicker
				disabled={ isExistingMonitor }
				groupClassname='menu-group'
				groupItemClassname='menu-group-item'
				hidden={ menuOptions === 'hide' }
				message={ COPY_SPECIFIC.NO_UNMONITORED_APPS_FOUND }
				name={
					fieldId === undefined ? [ 'monitorConfig', 'monitorTypeId' ] :
						[ fieldId, 'monitorConfig', 'monitorTypeId' ]
				}
				onChange={ handleOnTypeChange }
				options={ monitorTypeOptions }
				title={ isExistingMonitor ?
					SETTINGS_DATA_SECURITY_COPY.MONITOR_TYPE :
					SETTINGS_DATA_SECURITY_COPY.SELECT_MONITOR_TYPE }
				value={ currentMonitor.monitorConfig?.monitorTypeId } />
			<When condition={ showRecommendedActionTemplates && isTemplatesEnabled && !currentMonitor.id }>
				<VisualPicker
					groupClassname='template-group'
					groupItemClassname='template-group-item'
					name={
						fieldId === undefined ? [ 'templateId' ] :
							[ fieldId, 'templateId' ]
					}
					onChange={ handleOnTemplateChange }
					options={ templateTypeOptions }
					title={ COPY_SPECIFIC.RECOMMENDED_ACTION_TEMPLATES }
					value={ currentMonitor.templateId } />
			</When>
			<When condition={ selectedMonitorMenuConfig?.applicationRequired }>
				<MonitorApplication
					editing={ editing }
					fieldId={ fieldId }
					hidden={ Boolean(monitor?.id) || hideApplications }
					monitor={ currentMonitor }
					onChange={ (appId) => setSelectedMonitor({
						...currentMonitor,
						applicationId: appId
					}) } />
			</When>
			<When
				condition={
					currentMonitor.templateId ||
					(currentMonitor.monitorConfig?.monitorTypeId &&
					(isNumber(currentMonitor.applicationId) || !selectedMonitorMenuConfig?.applicationRequired))
				}>
				<Space direction='vertical'>
					<MonitorDetails
						editing={ editing }
						fieldId={ fieldId }
						hideName={ Boolean(currentMonitor.templateId) }
						monitor={ currentMonitor as DataSecurityMonitor }
						noMargin={ !selectedMonitorMenuConfig?.applicationRequired && menuOptions === 'hide' } />
					<MonitorNotifications
						editing={ editing }
						fieldId={ fieldId }
						schedule={ currentMonitor?.notificationSchedule }
						stakeholderIds={ currentMonitor?.stakeholderIds } />
				</Space>
			</When>
		</Space>
	);
};
